import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, MessagesSquare } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupTopics, type AdminTopic } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.10 — Forum topiclari. GET /admin/groups/:id/topics (backend `8310afb`).
 * `is_forum` yoqilgan guruhning mavzulari (General avto-yaratiladi).
 */
export function GroupTopics() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminTopic>[]>(
    () => [
      { key: 'topic_no', header: '#', type: 'text', width: 70 },
      {
        key: 'title',
        header: t.col.title,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="inline-flex items-center gap-1.5">
            {r.icon_emoji ? <span>{r.icon_emoji}</span> : null}
            <span>{r.title || '—'}</span>
          </span>
        ),
      },
      { key: 'is_general', header: t.groups.general, type: 'bool', width: 110 },
      { key: 'pinned', header: t.groups.pinned, type: 'bool', width: 100 },
      {
        key: 'closed',
        header: t.col.status,
        type: 'enum',
        width: 130,
        accessor: (r) => (r.closed ? 'closed' : 'open'),
        options: [
          { value: 'open', label: t.status.active, status: 'active' },
          { value: 'closed', label: t.groups.closed, status: 'deleted' },
        ],
      },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupTopics(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.topics}
        screenId="A4.10"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminTopic>
        screenId="A4.10"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <MessagesSquare size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
